import React, { useState } from 'react';
import { Text, Collapse, UnstyledButton, Stack, Group } from '@mantine/core';
import {
    IconAlertTriangle,
    IconBell,
    IconClock,
    IconUser,
    IconChevronDown,
    IconChevronUp,
} from '@tabler/icons-react';
import dayjs from 'dayjs';

import { PriceChangeRequestEscalationEvent } from '../../../codegen/graphql';
import { PropagationStopper, When } from '../../common';

type Props = {
    events?: PriceChangeRequestEscalationEvent[] | null;
};

type EscalationEventCardProps = {
    event: PriceChangeRequestEscalationEvent;
};

function EscalationEventCard({ event }: EscalationEventCardProps) {
    const isActive = !event.resolved;
    const breachedByName =
        event.breachedByName.charAt(0).toUpperCase() +
        event.breachedByName.slice(1);

    const cardClassName = isActive
        ? 'border-[#D4A574] bg-[#FFF9F2]'
        : 'border-[#2F9E85] bg-[#E6FCF5]';
    const textClassName = isActive ? 'text-[#862E0B]' : 'text-[#087F5B]';
    const iconColor = isActive ? '#E8590C' : '#20c997';

    return (
        <Stack
            className={`rounded-[12px] border border-solid p-4 ${cardClassName}`}
            gap="sm"
        >
            <Group className="items-center gap-2" wrap="nowrap">
                <IconAlertTriangle size={16} color={iconColor} />
                <Text size="sm" fw={600} className={textClassName}>
                    {event.resolved
                        ? 'Resolved Escalation'
                        : 'Active Escalation'}
                </Text>
            </Group>

            <Text size="sm" className={`leading-snug ${textClassName}`}>
                {breachedByName} exceeded the {event.slaLabel} SLA without
                actioning this request.
            </Text>

            <Group className="flex-wrap items-center gap-x-6 gap-y-2">
                <Group className="items-center gap-1.5" wrap="nowrap">
                    <IconBell size={14} color="#868E96" />
                    <Text size="xs" className="text-[#495057]">
                        <Text
                            component="span"
                            fw={700}
                            className="text-[#343A40]"
                        >
                            Notified:
                        </Text>{' '}
                        {event.escalatedToName}
                        <When condition={!!event.escalatedToDesignation}>
                            {` (${event.escalatedToDesignation})`}
                        </When>
                    </Text>
                </Group>
                <Group className="items-center gap-1.5" wrap="nowrap">
                    <IconClock size={14} color="#868E96" />
                    <Text size="xs" className="text-[#495057]">
                        {dayjs(event.escalatedAt).format('D MMM YYYY · HH:mm')}
                    </Text>
                </Group>
            </Group>

            <Group className="items-center gap-1.5" wrap="nowrap">
                <IconUser size={14} color="#868E96" />
                <Text size="xs" className="text-[#495057]">
                    <Text component="span" fw={700} className="text-[#343A40]">
                        {event.resolved
                            ? 'Resolved by:'
                            : 'Intervention required:'}
                    </Text>{' '}
                    <When condition={event.resolved}>
                        <Text component="span" className={textClassName}>
                            {event.resolvedByName}
                            <When condition={!!event.resolvedAt}>
                                {` on ${dayjs(event.resolvedAt).format(
                                    'D MMM YYYY · HH:mm',
                                )}`}
                            </When>
                        </Text>
                    </When>
                    <When condition={!event.resolved}>
                        <Text component="span" className={textClassName}>
                            Yes — awaiting {event.escalatedToName}
                        </Text>
                    </When>
                </Text>
            </Group>
        </Stack>
    );
}

function PriceChangeRequestEscalationEventsDropdown({ events }: Props) {
    const [opened, setOpened] = useState(false);
    const escalationEvents = events ?? [];
    const anyActive = escalationEvents.some(event => !event.resolved);

    return (
        <When condition={escalationEvents.length > 0}>
            <PropagationStopper>
                <Stack className="mt-2" gap="xs">
                    <UnstyledButton
                        onClick={event => {
                            event.stopPropagation();
                            setOpened(current => !current);
                        }}
                        className="flex items-center gap-1 text-xs text-gray-500 transition-colors hover:text-gray-700"
                    >
                        <When
                            condition={opened}
                            fallback={<IconChevronDown size={14} />}
                        >
                            <IconChevronUp size={14} />
                        </When>
                        <Text
                            component="span"
                            size="xs"
                            className="text-gray-500"
                        >
                            {escalationEvents.length} escalation event
                            {escalationEvents.length > 1 ? 's' : ''} ·{' '}
                            {anyActive ? 'Intervention required' : 'Resolved'}
                        </Text>
                    </UnstyledButton>

                    <Collapse in={opened}>
                        <Stack gap="sm">
                            {escalationEvents.map(event => (
                                <EscalationEventCard
                                    key={`${event.escalatedAt}-${
                                        event.escalatedToName
                                    }-${event.breachedByName}-${
                                        event.resolvedAt ?? 'pending'
                                    }`}
                                    event={event}
                                />
                            ))}
                        </Stack>
                    </Collapse>
                </Stack>
            </PropagationStopper>
        </When>
    );
}

export default PriceChangeRequestEscalationEventsDropdown;
